import DotsLoader from '@/components/dots-loader'
import { MultiSelect } from '@/components/multi-select'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FileUpload } from '@/components/ui/file-upload'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useFilters } from '@/hooks/useFilters'
import { listContactsQuery } from '@/queries/contact.query'
import { listTagsQuery } from '@/queries/tags.query'
import { ContactService } from '@/services/contacts.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Suspense } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

const formSchema = z.object({
  file: z.custom<FileList>()
    .refine((files) => files instanceof FileList && files.length > 0,
      'Por favor, envie um arquivo')
    .refine((files) => files[0]?.size <= 5 * 1024 * 1024,
      'O arquivo deve ser menor que 5MB')
    .refine(
      (files) => ['text/csv'].includes(files[0]?.type),
      'Por favor, envie um arquivo csv.',
    ),
  tagIds: z.array(z.string()).max(3, 'Máximo de 3 segmentos por importação').optional(),
})

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type TagSelectorProps = {
  form: UseFormReturn<z.infer<typeof formSchema>>;
}

function TagSelector({ form }: TagSelectorProps) {
  const { data: tagsData } = useSuspenseQuery(listTagsQuery);

  return (
    <FormField
      control={form.control}
      name='tagIds'
      render={({ field }) => (
        <FormItem>
          <FormLabel>Tags</FormLabel>
          <FormControl>
            <MultiSelect
              placeholder="Selecione as tags"
              options={tagsData.map(tag => ({
                label: tag.name,
                value: tag.id.toString()
              }))}
              onValueChange={field.onChange}
              defaultValue={field.value || []}
              value={field.value || []}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function ContactsImportDialog({ open, onOpenChange }: Props) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      file: undefined,
      tagIds: [],
    },
  })
  const queryClient = useQueryClient();
  const { filters } = useFilters('/_authenticated/contacts/');

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const [file] = data.file;

    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      if (data.tagIds && data.tagIds.length > 0) {
        formData.append('tagIds', data.tagIds.join(','));
      }
      onOpenChange(false)
      const toastId = toast.loading('Importando contatos...', {
        description: 'Este processo pode levar alguns segundos.',
      });
      try {
        const { created } = await ContactService.importContacts(formData);
        toast.success(`Contatos importados`, {
          description: `${created} contatos importados`,
          id: toastId
        })
      } catch (error) {
        toast.error('Ocorreu um erro ao importar contatos', {
          id: toastId
        })
      } finally {
        queryClient.invalidateQueries({ queryKey: listContactsQuery(filters).queryKey });
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        onOpenChange(val)
        form.reset()
      }}
    >
      <DialogContent className='gap-2 sm:max-w-xl'>
        <DialogHeader className='text-left'>
          <DialogTitle>Importar contatos</DialogTitle>
          <DialogDescription>
            Importe contatos rapidamente de um arquivo CSV.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form id='task-import-form' onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mb-4">
            <FormField
              control={form.control}
              name='file'
              render={({ field: { onChange, value, ...field } }) => (
                <FormItem className='mb-2 space-y-1'>
                  <FormControl>
                    <FileUpload
                      {...field}
                      onChange={(e) => {
                        onChange(e.target.files);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Suspense fallback={
              <div className="flex items-center space-x-2">
                <FormLabel className="opacity-50">Carregando tags</FormLabel>
                <DotsLoader />
              </div>
            }>
              <TagSelector form={form} />
            </Suspense>
          </form>
        </Form>
        <DialogFooter className='gap-2 sm:gap-0'>
          <DialogClose asChild>
            <Button variant='outline'>Fechar</Button>
          </DialogClose>
          <Button type='submit' form='task-import-form'>
            Importar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
